import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useMemo } from 'react';
import { defaultPostOfficeContextValue, InternalPostOfficeContextProvider } from '../post-office-context';
import { defaultEnvironmentValues, PostOfficeEnvironmentProvider } from '../post-office-environment';
import { RouteListenerProvider } from '../post-office-route/listener';
import { PostOfficeScreenContextProvider } from '../post-office-screen-context';
import { TriggerContextProvider } from '../post-office-trigger';
import { useStableObject } from '../util/use-stable-object';
import { createMessagePlacementValues, MessagePlacementProvider } from './message-placement';
import { generateEnvironmentConfigWithOverrides } from './util/get-environment-with-overrides';

/**
 * A provider component for managing the context and configuration of all post office placements.
 * This should only appear once at a high level in your product
 *
 * @component
 * @param {Object} props - The properties passed to the PostOfficeProvider component.
 * @param {PostOfficeContextConfig} props.context - Configuration related to the current post office context,
 * including any relevant state or settings specific to the current session or user.
 * @param {PostOfficeEnvironmentConfig} props.environment - Configuration related to the environment
 * the application is currently running in. This includes the environment type (e.g., development, production)
 * and any optional remote module configuration or environment-specific overrides.
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 *
 * **Example Usage:**
 * ```jsx
 * <PostOfficeProvider
 *   context={{ current: { ... } }}
 *   environment={{
 *     env: "production",
 *     overrides: { ... }
 *   }}
 * >
 *   <YourApp />
 * </PostOfficeProvider>
 * ```
 */
export var PostOfficeProvider = function PostOfficeProvider(_ref) {
  var envWithOverrides = _ref.environment,
    context = _ref.context,
    children = _ref.children;
  var stableEnvironment = useStableObject(generateEnvironmentConfigWithOverrides(defaultEnvironmentValues, envWithOverrides));
  var stableContext = useStableObject(composeConfig(defaultPostOfficeContextValue, context.current));
  var memoizedMessagePlacementValues = useMemo(function () {
    return createMessagePlacementValues();
  }, []);
  return /*#__PURE__*/React.createElement(PostOfficeEnvironmentProvider, {
    value: stableEnvironment
  }, /*#__PURE__*/React.createElement(InternalPostOfficeContextProvider, {
    value: stableContext
  }, /*#__PURE__*/React.createElement(PostOfficeScreenContextProvider, null, /*#__PURE__*/React.createElement(MessagePlacementProvider, {
    value: memoizedMessagePlacementValues
  }, /*#__PURE__*/React.createElement(RouteListenerProvider, null, /*#__PURE__*/React.createElement(TriggerContextProvider, null, children))))));
};
var composeConfig = function composeConfig(base) {
  for (var _len = arguments.length, rest = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    rest[_key - 1] = arguments[_key];
  }
  return rest.reduce(function (acc, v) {
    // Bring logic inline with lodash merge
    // Iterator (right) properties that resolve to undefined are skipped if an Accumulator (left) value already exists
    for (var key in v) {
      if (typeof v[key] === 'undefined') {
        delete v[key];
      }
    }
    return _objectSpread(_objectSpread({}, acc), v);
  }, _objectSpread({}, base));
};